import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  X, Send, Sparkles, Plus, Trash2, ChevronDown,
  FileText, Image as ImageIcon, RotateCcw, Settings, Check,
  ChevronLeft, ChevronRight, Copy, ClipboardList,
  Paperclip,
} from 'lucide-react';
import { API, useAuth } from '../App';
import ProposalReview from './ProposalReview';
import PlanReviewInline from './PlanReviewInline';
import { uploadDocument } from '../documentUpload';

const SIDEBAR_WIDTH = 380;
const WELCOME_PROMPT = 'How does this work? How do you help me plan?';

function fromStoredMessage(m) {
  const payload = m.content;
  const text = typeof payload === 'object' && payload !== null ? (payload.text || '') : (typeof payload === 'string' ? payload : '');
  return { id: m.id, persistedId: m.id, role: m.role, content: text.replace(/\n?\[Attached images: [^\]]+\]/g, ''),
    apiContent: text, imageResults: typeof payload === 'object' && payload !== null ? (payload.imageResults || []) : [],
    planReview: payload?.planReview || null, authorUserId: m.author_user_id, authorEmail: m.author_email, cards: [] };
}

const AiSidebar = forwardRef(function AiSidebar({ plan, navStack, currentParent, onCreateBox, onOpenSettings, isMobile, openOnWelcome, showStarterPrompt }, ref) {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!openOnWelcome) return;
    setOpen(true);
  }, [openOnWelcome]);
  const [messages, setMessages] = useState([]); // [{id, role, content, chunks}]
  const [input, setInput] = useState('');
  const [cardContext, setCardContext] = useState(null); // { id, title, content } of focused card
  const [proposals, setProposals] = useState([]);
  const [modifyProposal, setModifyProposal] = useState(null);
  const [inheritedOpen, setInheritedOpen] = useState(false);
  const [inheritedScopes, setInheritedScopes] = useState([]);
  const [inheritedNotes, setInheritedNotes] = useState({});
  const [inheritedError, setInheritedError] = useState('');
  const [contextWarning, setContextWarning] = useState('');
  const [attachedDocuments, setAttachedDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [attachmentError, setAttachmentError] = useState('');
  const [attachmentUploads, setAttachmentUploads] = useState([]);
  const [draggingAttachment, setDraggingAttachment] = useState(false);
  const attachmentInputRef = useRef(null);

  // Expose openWithContext + _onCardCreated to parent via ref
  const onCardCreatedRef = useRef(null);
  useImperativeHandle(ref, () => ({
    openWithContext(box) {
      // Strip HTML tags from content
      const rawContent = box.content || '';
      const strippedContent = rawContent.replace(/<[^>]*>/g, ' ').replace(/\s{2,}/g, ' ').trim();
      setCardContext({ id: box.id, title: box.title, content: strippedContent });
      setInput('');
      setOpen(true);
      // Auto-send the card content as the first user message after sidebar opens
      setTimeout(() => sendCardContext({ id: box.id, title: box.title, content: strippedContent }), 80);
    },
    // MindMapCanvas sets this to trigger a canvas refresh after cards are created
    set _onCardCreated(cb) { onCardCreatedRef.current = cb; },
    get _onCardCreated() { return onCardCreatedRef.current; },
  }));

  const [streaming, setStreaming] = useState(false);
  const [streamStatus, setStreamStatus] = useState(''); // 'thinking' | 'writing' | 'creating' | ''
  const [settings, setSettings] = useState(null);
  const [unread, setUnread] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const loadInherited = useCallback(async () => {
    if (!token || !plan?.id || !currentParent || currentParent === 'root') { setInheritedScopes([]); return; }
    try {
      const response = await fetch(`${API}/api/plans/${plan.id}/inherited-context?parentId=${encodeURIComponent(currentParent)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not load inherited context');
      setInheritedScopes(data);
      setInheritedNotes(Object.fromEntries(data.map(scope => [scope.id, scope.userNote || ''])));
      setInheritedError('');
    } catch (error) { setInheritedError(error.message); }
  }, [token, plan?.id, currentParent]);

  useEffect(() => { if (inheritedOpen) loadInherited(); }, [inheritedOpen, loadInherited]);

  async function saveInheritedNote(scope) {
    try {
      const response = await fetch(`${API}/api/plans/${plan.id}/inherited-context/${encodeURIComponent(scope.id)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentParent, userNote: inheritedNotes[scope.id] || '' }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not save correction');
      await loadInherited();
    } catch (error) { setInheritedError(error.message); }
  }

  // Programmatic starter text needs the same auto-sizing as typed text.
  useEffect(() => {
    if (!open || streaming || !inputRef.current) return;
    const textarea = inputRef.current;
    const resize = () => {
      textarea.style.height = '0px';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    };
    // The sidebar animates wider after opening. Its initially narrow composer
    // wraps the placeholder and can leave a blank 120px textarea behind.
    let previousWidth = 0;
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === previousWidth) return;
      previousWidth = entry.contentRect.width;
      resize();
    });
    observer.observe(textarea.parentElement);
    resize();
    return () => observer.disconnect();
  }, [input, open, streaming]);

  // Load settings once
  useEffect(() => {
    fetch(`${API}/api/settings`).then(r => r.json()).then(setSettings);
  }, []);

  // Reload chat history whenever the level (currentParent) changes
  useEffect(() => {
    const scope = currentParent || 'root';
    setProposals([]);
    setAttachedDocuments([]);
    setAttachmentUploads([]);
    if (token) fetch(`${API}/api/plans/${plan.id}/ai-proposals?parentId=${encodeURIComponent(scope)}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(response => response.ok ? response.json() : []).then(setProposals).catch(() => {});
    fetch(`${API}/api/plans/${plan.id}/chat?parentId=${encodeURIComponent(scope)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(msgs => {
        if (showStarterPrompt && msgs.length === 0) {
          setInput(previous => previous || WELCOME_PROMPT);
        }
        setMessages(msgs.map(fromStoredMessage));
      });
  }, [plan.id, currentParent, token, showStarterPrompt]);

  // Shared chat updates while another editor is working. Never overwrite an active stream.
  useEffect(() => {
    if (!open || streaming || !token) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const response = await fetch(`${API}/api/plans/${plan.id}/chat?parentId=${encodeURIComponent(currentParent || 'root')}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!response.ok || cancelled) return;
        const stored = (await response.json()).map(fromStoredMessage);
        if (cancelled) return;
        setMessages(previous => previous.length === stored.length && previous.every((message, index) => message.id === stored[index].id && message.content === stored[index].content)
          ? previous : stored);
      } catch {}
    };
    const timer = setInterval(refresh, 4000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [open, streaming, token, plan.id, currentParent]);

  async function actOnProposal(proposal, action) {
    if (action === 'cancel') {
      setProposals(previous => previous.filter(item => item.id !== proposal.id));
    } else {
      setProposals(previous => previous.map(item => item.id === proposal.id ? { ...item, busy: true, error: null } : item));
    }
    try {
      const response = await fetch(`${API}/api/ai-proposals/${proposal.id}${action === 'approve' ? '/approve' : ''}`, {
        method: action === 'approve' ? 'POST' : 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || `HTTP ${response.status}`);
      setProposals(previous => previous.filter(item => item.id !== proposal.id));
      if (action === 'approve') {
        let refreshFailed = false;
        try { await ref?.current?._onCardCreated?.(result.card); }
        catch { refreshFailed = true; }
        setMessages(previous => [...previous, { id: `${proposal.id}-approved`, role: 'assistant', content: `✓ ${proposal.action === 'create' ? 'Created' : 'Updated'} "${result.card.title}".${refreshFailed ? ' View refresh failed; reload the page to see it.' : ''}` }]);
      }
      return true;
    } catch (error) {
      if (action === 'cancel') {
        setProposals(previous => [...previous, { ...proposal, busy: false, error: error.message }]);
      } else {
        setProposals(previous => previous.map(item => item.id === proposal.id ? { ...item, busy: false, error: error.message } : item));
      }
      return false;
    }
  }

  // When sidebar opens: re-fetch settings, clear unread, focus input, scroll to bottom
  useEffect(() => {
    if (open) {
      fetch(`${API}/api/settings`).then(r => r.json()).then(setSettings);
      setUnread(false);
      setTimeout(() => {
        inputRef.current?.focus();
        bottomRef.current?.scrollIntoView({ behavior: 'instant' });
      }, 80);
    }
  }, [open]);

  // Keep newly staged review cards visible even when the model emits no more text.
  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, proposals.length]);

  // Called automatically when opening via card AI button — sends card content as first message
  async function sendCardContext({ id, title, content }) {
    if (streaming) return;
    const messageText = `Here is the content of my "${title}" card:\n\n${content}`;
    const userMsg = { id: Date.now() + 'u', role: 'user', content: messageText, authorUserId: user?.id, authorEmail: user?.email };
    const assistantId = Date.now() + 'a';
    setMessages(prev => [...prev, userMsg, { id: assistantId, role: 'assistant', content: '', cards: [], authorEmail: user?.email }]);
    setStreaming(true);
    setStreamStatus('thinking');
    setContextWarning('');

    let currentBoxes = [];
    try {
      const scope = currentParent || 'root';
      const boxRes = await fetch(`${API}/api/plans/${plan.id}/boxes/${scope}/children`);
      currentBoxes = await boxRes.json();
    } catch (e) {}

    const planContext = {
      planTitle: plan.title,
      breadcrumb: navStack.length > 0 ? navStack.map(f => f.title).join(' › ') : null,
      currentLevel: navStack.length > 0 ? navStack[navStack.length - 1].title : 'root',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      boxes: [],
      focusedCardId: id || null,
    };

    try {
      const controller = new AbortController();
      abortRef.current = controller;
      const resp = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        signal: controller.signal,
        body: JSON.stringify({
          messages: [{ role: 'user', content: messageText }],
          model: settings?.chatModel || 'anthropic/claude-sonnet-4-6',
          planContext,
          planId: plan.id,
          parentId: currentParent || 'root',
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '', fullText = '', createdCards = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n'); buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') break;
          try {
            const evt = JSON.parse(payload);
            if (evt.tool === 'chat_message_id') setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, persistedId: evt.id } : m));
            else if (evt.tool === 'plan_review_suggestion') setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, planReview: evt.suggestion } : m));
            else if (evt.tool === 'context_summarizing') setStreamStatus('checking parent context…');
            else if (evt.tool === 'context_ready') setStreamStatus('thinking');
            else if (evt.tool === 'context_warning') setContextWarning(evt.message || 'Parent conversation context could not be refreshed.');
            else if (evt.text) { if (streamStatus !== 'writing') setStreamStatus('writing'); fullText += evt.text; }
            else if (evt.tool === 'ai_proposal') setProposals(previous => [...previous, evt.proposal]);
            else if (evt.error) fullText += `\n\n*Error: ${evt.error}*`;
          } catch (e) {}
        }
        const ft = fullText; const cc = [...createdCards];
        setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: ft, cards: cc } : m));
        // Refresh canvas once after all cards are created
        if (cc.length > 0) { ref?.current?._onCardCreated?.(); }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        setMessages(prev => prev.map(m => m.id === assistantId && !m.content ? { ...m, content: `*Error: ${e.message}*` } : m));
      }
    }
    setStreaming(false);
    setStreamStatus('');
    abortRef.current = null;
    if (inheritedOpen) loadInherited();
  }

  // Build planContext fresh at send time so it includes current boxes
  async function attachFiles(files) {
    if (!files.length || uploadingDocument) return;
    if (attachedDocuments.filter(document => document.mime_type?.startsWith('image/')).length + files.filter(file => file.type.startsWith('image/') || /\.(png|jpe?g|webp|gif)$/i.test(file.name)).length > 4) {
      setAttachmentError('Attach up to four images per prompt.');
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
      return;
    }
    const queued = files.map((file, index) => ({ id: `${Date.now()}-${index}`, file, isImage: file.type.startsWith('image/') || /\.(png|jpe?g|webp|gif)$/i.test(file.name), phase: 'queued', percent: 0 }));
    setAttachmentUploads(queued);
    setUploadingDocument(true); setAttachmentError('');
    try {
      for (const item of queued) {
        try {
          const result = await uploadDocument({ planId: plan.id, token, file: item.file, scope: 'chat', parentId: currentParent || 'root', onProgress: progress =>
            setAttachmentUploads(previous => previous.map(entry => entry.id === item.id ? { ...entry, ...progress } : entry)) });
          setAttachedDocuments(previous => [...previous, result]);
          setAttachmentUploads(previous => previous.map(entry => entry.id === item.id ? { ...entry, phase: 'ready', percent: 100 } : entry));
        } catch (uploadError) {
          setAttachmentUploads(previous => previous.map(entry => entry.id === item.id ? { ...entry, phase: 'failed', error: uploadError.message } : entry));
        }
      }
    } finally {
      setUploadingDocument(false);
      setAttachmentUploads(previous => previous.filter(item => item.phase === 'failed'));
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  }

  function hasDraggedFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes('Files');
  }

  function handleAttachmentDrop(event) {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    setDraggingAttachment(false);
    if (!streaming) attachFiles(Array.from(event.dataTransfer.files || []));
  }

  async function removeAttachment(document) {
    setAttachedDocuments(previous => previous.filter(item => item.id !== document.id));
    const response = await fetch(`${API}/api/plans/${plan.id}/documents/${document.id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    }).catch(() => null);
    if (!response?.ok) setAttachmentError(`Could not remove ${document.filename} from this chat. You can delete it in the Library.`);
  }

  async function send() {
    if ((!input.trim() && !attachedDocuments.length) || streaming || uploadingDocument) return;

    async function recordProposal(proposal) {
      setProposals(previous => [...previous.filter(item => item.id !== proposal.id && !(proposal.supersededIds || []).includes(item.id)), proposal]);
    }

    // If there's a card context, prepend it to the API message but show them separately in the UI
    const question = input.trim() || (attachedDocuments.some(document => document.mime_type?.startsWith('image/')) ? 'Please examine the attached image and help me plan.' : 'Please read the attached document and help me plan.');
    const attachedImages = attachedDocuments.filter(document => document.mime_type?.startsWith('image/'));
    const attachedLabel = attachedDocuments.length ? `\n\n[Attached files: ${attachedDocuments.map(document => document.filename).join(', ')}]` : '';
    const imageMarker = attachedImages.length ? `\n[Attached images: ${attachedImages.map(image => image.id).join(',')}]` : '';
    const revisingProposal = modifyProposal;
    const apiContent = (modifyProposal
      ? modifyProposal.payload.content_type === 'image'
        ? `[Image proposal revision] Revise the image for card "${modifyProposal.payload.title}"${modifyProposal.card_id ? ` (id: ${modifyProposal.card_id})` : ''}. Generate the revised image using create_image with that exact card_title. The app will stage an update if this card now exists, otherwise a new-card proposal. User's requested modification: ${input.trim()}. Do not claim a card was changed before approval.`
        : `Revise this CANCELLED ${modifyProposal.action} proposal for card "${modifyProposal.payload.title}". Original proposed full content:\n${modifyProposal.payload.content || ''}\n\nUser's requested modification: ${input.trim()}\n\nPrepare a NEW proposal for the same target${modifyProposal.card_id ? ` (id: ${modifyProposal.card_id})` : ''}; do not apply any changes yet.`
      : cardContext
        ? `[Card context — "${cardContext.title}"]:${cardContext.content ? '\n' + cardContext.content : ''}\n\n[My question]: ${question}`
        : question) + attachedLabel + imageMarker;

    // Display message: show card context inline above the user question
    const displayContent = question + attachedLabel;
    const userMsg = { id: Date.now() + 'u', role: 'user', content: displayContent, apiContent, cardContext: cardContext || null, authorUserId: user?.id, authorEmail: user?.email };
    const assistantId = Date.now() + 'a';
    console.log('[IdeaCanvas AI] Sending message:', displayContent);
    console.log('[IdeaCanvas AI] Model:', settings?.chatModel || 'anthropic/claude-sonnet-4-6');
    setMessages(prev => [...prev, userMsg, { id: assistantId, role: 'assistant', content: '', cards: [], authorEmail: user?.email }]);
    setInput('');
    const sendingDocuments = attachedDocuments;
    setAttachedDocuments([]);
    setModifyProposal(null);
    setCardContext(null); // clear card context block once sent
    setStreaming(true);
    setStreamStatus('thinking');
    setContextWarning('');

    // Build history with apiContent (includes card context) for the last user message
    const history = [...messages, userMsg].map(m => ({ role: m.role, content: m.apiContent || m.content }));

    // Fetch plan summary for full-plan AI context
    let planSummary = '';
    try {
      const sr = await fetch(`${API}/api/plans/${plan.id}/summary`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const sd = await sr.json();
      planSummary = sd.summary || '';
    } catch (e) {
      console.warn('[IdeaCanvas AI] Could not fetch plan summary:', e.message);
    }

    // Optionally fetch visible cards content
    const visibleCards = [];

    const planContext = {
      planTitle: plan.title,
      planId: plan.id,
      parentId: currentParent || 'root',
      breadcrumb: navStack.length > 0 ? navStack.map(f => f.title).join(' › ') : null,
      currentLevel: navStack.length > 0 ? navStack[navStack.length - 1].title : 'root',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      planSummary,
      visibleCards,
      boxes: [],
      focusedCardId: cardContext?.id || null,
    };

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      console.log('[IdeaCanvas AI] Fetching /api/chat...');
      const resp = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        signal: controller.signal,
        body: JSON.stringify({
          messages: history,
          model: settings?.chatModel || 'anthropic/claude-sonnet-4-6',
          planContext,
          planId: plan.id,
          parentId: currentParent || 'root',
          documentIds: sendingDocuments.filter(document => !document.mime_type?.startsWith('image/')).map(document => document.id),
        }),
      });

      console.log('[IdeaCanvas AI] Response status:', resp.status, resp.headers.get('content-type'));
      if (!resp.ok) {
        const errText = await resp.text();
        console.error('[IdeaCanvas AI] HTTP error:', resp.status, errText);
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200)}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let fullText = '';
      let createdCards = [];
      let chunkCount = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) { console.log('[IdeaCanvas AI] Stream done. Chunks received:', chunkCount); break; }
        chunkCount++;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') { console.log('[IdeaCanvas AI] [DONE] received'); break; }
          try {
            const evt = JSON.parse(payload);
            if (evt.tool === 'chat_message_id') {
              setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, persistedId: evt.id } : m));
            } else if (evt.tool === 'card_proposals_preparing') {
              setStreamStatus('creating');
            } else if (evt.tool === 'replace_text') {
              fullText = evt.text || '';
            } else if (evt.tool === 'plan_review_suggestion') {
              setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, planReview: evt.suggestion } : m));
            } else if (evt.tool === 'context_summarizing') {
              setStreamStatus('checking parent context…');
            } else if (evt.tool === 'context_ready') {
              setStreamStatus('thinking');
            } else if (evt.tool === 'context_warning') {
              setContextWarning(evt.message || 'Parent conversation context could not be refreshed.');
            } else if (evt.error) {
              console.error('[IdeaCanvas AI] Server error event:', evt.error);
              fullText += `\n\n*Error: ${evt.error}*`;
            } else if (evt.text) {
              if (streamStatus !== 'writing') setStreamStatus('writing');
              fullText += evt.text;
            } else if (evt.tool === 'ai_proposal') {
              await recordProposal(evt.proposal);
            } else if (evt.tool === 'image_generating') {
              console.log('[IdeaCanvas AI] Generating image:', evt.prompt);
              setStreamStatus(evt.referenceCount ? `generating with ${evt.referenceCount} reference image${evt.referenceCount === 1 ? '' : 's'}…` : 'generating image…');
            } else if (evt.tool === 'image_generated') {
              console.log('[IdeaCanvas AI] Image generated:', evt.url ? 'success' : 'error', evt.error || '');
              if (evt.url) {
                // Append generated image block to the message
                const imgBlock = { type: 'image_generated', prompt: evt.prompt, url: evt.url, card_title: evt.card_title, referenceCount: evt.referenceCount || 0 };
                setMessages(prev => prev.map(m =>
                  m.id === assistantId
                    ? { ...m, imageResults: [...(m.imageResults || []), imgBlock] }
                    : m
                ));
                // Preparing an image card also requires explicit approval.
                if (evt.card_title && plan?.id) {
                  try {
                    const createResp = await fetch(`${API}/api/plans/${plan.id}/ai-proposals`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                      body: JSON.stringify({ action: 'generated_image', parentId: currentParent || 'root', cardId: revisingProposal?.payload.content_type === 'image' ? revisingProposal.card_id : null, payload: { title: evt.card_title, content_type: 'image', content: evt.url } }),
                    });
                    if (!createResp.ok) throw new Error((await createResp.json().catch(() => ({}))).error || `HTTP ${createResp.status}`);
                    const prepared = await createResp.json();
                    await recordProposal(prepared);
                  } catch (autoCreateErr) {
                    console.error('[IdeaCanvas AI] Image card proposal error:', autoCreateErr.message);
                    fullText += `\n\n*Could not prepare an image card proposal: ${autoCreateErr.message}*`;
                  }
                }
              } else {
                const errBlock = { type: 'image_generated', prompt: evt.prompt, url: null, error: evt.error };
                setMessages(prev => prev.map(m =>
                  m.id === assistantId
                    ? { ...m, imageResults: [...(m.imageResults || []), errBlock] }
                    : m
                ));
              }
            } else if (evt.tool === 'url_fetching') {
              console.log('[IdeaCanvas AI] Fetching URL:', evt.url);
              setStreamStatus('reading page…');
            } else if (evt.tool === 'url_fetched') {
              console.log('[IdeaCanvas AI] URL fetched:', evt.url, evt.error || 'ok');
              setStreamStatus('writing');
            } else if (evt.tool === 'image_searching') {
              console.log('[IdeaCanvas AI] Searching images for:', evt.query);
              setStreamStatus('searching images…');
            } else if (evt.tool === 'image_results') {
              console.log('[IdeaCanvas AI] Image results:', evt.images?.length, 'for query:', evt.query);
              // Append image results block to the message
              const imgBlock = { type: 'image_results', query: evt.query, images: evt.images || [] };
              setMessages(prev => prev.map(m =>
                m.id === assistantId
                  ? { ...m, imageResults: [...(m.imageResults || []), imgBlock] }
                  : m
              ));
            } else {
              console.debug('[IdeaCanvas AI] Unhandled event:', evt);
            }
          } catch (parseErr) {
            console.warn('[IdeaCanvas AI] Could not parse SSE line:', line, parseErr.message);
          }
        }
        const ft = fullText;
        const cc = [...createdCards];
        setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: ft, cards: cc } : m));
      }

      if (!open) setUnread(true);
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('[IdeaCanvas AI] Caught error:', e.name, e.message, e);
        setMessages(prev => prev.map(m => {
          if (m.id !== assistantId) return m;
          if (m.content && m.content.length > 0) return m;
          return { ...m, content: `*Error: ${e.message}*` };
        }));
      } else {
        console.log('[IdeaCanvas AI] Request aborted by user');
      }
    }
    setStreaming(false);
    setStreamStatus('');
    abortRef.current = null;
    if (inheritedOpen) loadInherited();
  }

  function stop() {
    abortRef.current?.abort();
    setStreaming(false);
  }

  async function copyAllMessages() {
    const text = messages
      .filter(m => m.content)
      .map(m => `${m.role === 'user' ? 'You' : 'AI'}: ${m.content}`)
      .join('\n\n');
    try { await navigator.clipboard.writeText(text); } catch (e) {}
  }

  async function clearHistory() {
    await fetch(`${API}/api/plans/${plan.id}/chat?parentId=${encodeURIComponent(currentParent || 'root')}`, { method: 'DELETE' });
    setMessages([]);
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  }

  const modelName = settings
    ? (settings.chatModel.split('/')[1] || settings.chatModel)
    : '…';

  return (
    <>
      {/* ── Sidebar panel — flex child, pushes canvas left ─────────────── */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="sidebar"
            initial={isMobile ? { y: '100%' } : { width: 0, opacity: 0 }}
            animate={isMobile ? { y: 0 } : { width: SIDEBAR_WIDTH, opacity: 1 }}
            exit={isMobile ? { y: '100%' } : { width: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 340, damping: 36 }}
            style={isMobile ? {
              position: 'fixed', left: 0, right: 0, bottom: 0, top: 0,
              zIndex: 300,
              width: '100%', flexShrink: 0, overflow: 'hidden',
              background: 'var(--bg-2)',
              display: 'flex', flexDirection: 'column',
            } : {
              width: SIDEBAR_WIDTH, flexShrink: 0, zIndex: 10, overflow: 'hidden',
              background: 'var(--bg-2)',
              borderLeft: '1px solid var(--border)',
              display: 'flex', flexDirection: 'column',
              boxShadow: '-8px 0 32px rgba(0,0,0,0.15)',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '14px 16px', borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Sparkles size={13} color="white" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>AI Research</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {modelName}{navStack.length > 0 ? (' · ' + navStack.map(f => f.title).join(' › ')) : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {currentParent && currentParent !== 'root' && <IconBtn onClick={() => setInheritedOpen(previous => !previous)} title="Inherited context from parent conversations">
                  <FileText size={13} />
                </IconBtn>}
                {messages.length > 0 && (
                  <>
                    <IconBtn onClick={copyAllMessages} title="Copy full conversation">
                      <ClipboardList size={13} />
                    </IconBtn>
                    {plan.role === 'owner' && <IconBtn onClick={clearHistory} title="Clear chat">
                      <Trash2 size={13} />
                    </IconBtn>}
                  </>
                )}
                <IconBtn onClick={onOpenSettings} title="Settings">
                  <Settings size={13} />
                </IconBtn>
                <IconBtn onClick={() => setOpen(false)} title="Close">
                  <ChevronDown size={13} />
                </IconBtn>
              </div>
            </div>

            {inheritedOpen && currentParent && currentParent !== 'root' && <div style={{ maxHeight: 300, overflowY: 'auto', padding: '10px 14px', borderBottom: '1px solid var(--border)', background: 'var(--bg-3)', fontSize: 11 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Inherited conversation context</div>
              <div style={{ color: 'var(--text-secondary)', marginBottom: 8 }}>Summaries refresh on the next child-level prompt only if an ancestor conversation changed.</div>
              {inheritedError && <div role="alert" style={{ color: '#ef4444' }}>{inheritedError}</div>}
              {!inheritedScopes.length && !inheritedError && <div>No ancestor conversation summaries yet.</div>}
              {inheritedScopes.map(scope => <div key={scope.id} style={{ padding: '8px 0', borderTop: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 700 }}>{scope.title}{scope.stale ? ' · Needs refresh' : ''}</div>
                {scope.generatedAt && <div style={{ color: 'var(--text-muted)' }}>Summarized {new Date(Number(scope.generatedAt)).toLocaleString()}</div>}
                {scope.summary && <div style={{ whiteSpace: 'pre-wrap', marginTop: 5 }}>{scope.summary}</div>}
                {!!scope.directives?.length && <div style={{ marginTop: 5 }}><strong>Instructions and decisions</strong><ul style={{ paddingLeft: 16, margin: '3px 0' }}>{scope.directives.map((item, index) => <li key={`${item.sourceId}-${index}`}>{item.text}</li>)}</ul></div>}
                {!scope.summary && !scope.directives?.length && <div style={{ color: 'var(--text-muted)', marginTop: 5 }}>No summarized conversation at this level yet.</div>}
                <textarea aria-label={`Correction for ${scope.title}`} value={inheritedNotes[scope.id] || ''} onChange={event => setInheritedNotes(previous => ({ ...previous, [scope.id]: event.target.value }))} placeholder="Add a correction or instruction for child levels…" rows={2} style={{ width: '100%', boxSizing: 'border-box', marginTop: 7, resize: 'vertical', background: 'var(--bg-2)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 6, padding: 5 }} />
                <button type="button" onClick={() => saveInheritedNote(scope)} disabled={(inheritedNotes[scope.id] || '') === (scope.userNote || '')} style={{ border: '1px solid var(--border)', background: 'var(--bg-4)', color: 'var(--text-primary)', borderRadius: 5, padding: '4px 8px', cursor: 'pointer' }}>Save correction</button>
              </div>)}
            </div>}
            {contextWarning && <div role="alert" style={{ padding: '6px 14px', color: '#b45309', fontSize: 11 }}>{contextWarning}</div>}

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Card context block — shown when opened via AI button on a card */}
              {cardContext && messages.length === 0 && (
                <div style={{
                  borderRadius: 12,
                  border: '1px solid #8b5cf640',
                  background: 'linear-gradient(135deg, #8b5cf608, #6366f108)',
                  overflow: 'hidden',
                }}>
                  {/* Header */}
                  <div style={{
                    padding: '10px 14px 8px',
                    borderBottom: '1px solid #8b5cf625',
                    display: 'flex', alignItems: 'center', gap: 7,
                  }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Sparkles size={10} color="white" />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#8b5cf6', letterSpacing: '0.3px' }}>CARD CONTEXT</span>
                    <button
                      onClick={() => setCardContext(null)}
                      title="Dismiss"
                      style={{
                        marginLeft: 'auto', background: 'none', border: 'none',
                        cursor: 'pointer', color: 'var(--text-faint)', display: 'flex', padding: 2,
                      }}>
                      <X size={12} />
                    </button>
                  </div>
                  {/* Card title + content */}
                  <div style={{ padding: '10px 14px 12px' }}>
                    {cardContext.title && (
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: cardContext.content ? 6 : 0 }}>
                        {cardContext.title}
                      </div>
                    )}
                    {cardContext.content && (
                      <div className="prose" style={{ fontSize: 12, lineHeight: 1.65, color: 'var(--text-secondary)', maxHeight: 200, overflowY: 'auto' }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{cardContext.content}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                  {/* Prompt hint */}
                  <div style={{
                    padding: '7px 14px 10px',
                    borderTop: '1px solid #8b5cf618',
                    fontSize: 11, color: 'var(--text-faint)', fontStyle: 'italic',
                  }}>
                    Ask a question about this card below ↓
                  </div>
                </div>
              )}

              {messages.length === 0 && !cardContext && (
                <EmptyChat />
              )}
              {messages.map(msg => (
                <ChatMessage
                  key={msg.id}
                  msg={msg}
                  currentUserId={user?.id}
                  streaming={streaming && msg.role === 'assistant' && msg === messages[messages.length - 1]}
                  onCreateBox={onCreateBox}
                  onCardCreated={card => ref?.current?._onCardCreated?.(card)}
                  focusedCard={cardContext}
                  token={token}
                  API={API}
                  onPropose={proposal => setProposals(previous => [...previous, proposal])}
                  planId={plan.id}
                  parentId={currentParent || 'root'}
                  plan={plan}
                  onPlanReviewChange={suggestion => setMessages(previous => previous.map(item => item.id === msg.id ? { ...item, planReview: suggestion } : item))}
                  onPlanReviewModify={proposal => { setModifyProposal(proposal); setInput(''); inputRef.current?.focus(); }}
                />
              ))}
              {proposals.map(proposal => (
                <ProposalReview key={proposal.id} proposal={proposal} onApprove={() => actOnProposal(proposal, 'approve')}
                  onCancel={() => actOnProposal(proposal, 'cancel')}
                  onModify={() => {
                    setModifyProposal(proposal);
                    setInput('');
                    inputRef.current?.focus();
                    actOnProposal(proposal, 'cancel').then(success => {
                      if (!success) setModifyProposal(current => current?.id === proposal.id ? null : current);
                    });
                  }} />
              ))}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div onDragEnterCapture={event => { if (hasDraggedFiles(event)) { event.preventDefault(); setDraggingAttachment(true); } }}
              onDragOverCapture={event => { if (hasDraggedFiles(event)) { event.preventDefault(); event.dataTransfer.dropEffect = streaming ? 'none' : 'copy'; } }}
              onDragLeaveCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setDraggingAttachment(false); }}
              onDropCapture={handleAttachmentDrop}
              style={{ padding: '12px 14px', borderTop: '1px solid var(--border)', flexShrink: 0, position: 'relative' }}>

              <input ref={attachmentInputRef} type="file" multiple hidden onChange={event => attachFiles(Array.from(event.target.files || []))} />
              {draggingAttachment && <div role="status" style={{ position: 'absolute', inset: 4, zIndex: 3, display: 'grid', placeItems: 'center', pointerEvents: 'none', border: '2px dashed #6366f1', borderRadius: 10, background: 'var(--bg-3)', color: '#6366f1', fontSize: 13, fontWeight: 600 }}>Drop files to attach</div>}
              {attachmentUploads.length > 0 && <div aria-live="polite" style={{ marginBottom: 7, fontSize: 11, color: 'var(--text-secondary)' }}>
                {attachmentUploads.map(item => <div key={item.id} style={{ padding: '4px 0' }}><div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.file.name}</span><span style={{ flexShrink: 0, color: item.phase === 'failed' ? '#ef4444' : 'inherit' }}>{item.phase === 'queued' ? 'Waiting…' : item.phase === 'uploading' ? `Uploading ${item.percent}%` : item.phase === 'processing' ? item.isImage ? 'Preparing image…' : 'Indexing text…' : item.phase === 'ready' ? 'Ready ✓' : 'Failed'}</span></div>{(item.phase === 'uploading' || item.phase === 'processing') && <div role="progressbar" aria-label={`Upload ${item.file.name}`} aria-valuenow={item.percent} aria-valuemin={0} aria-valuemax={100} style={{ height: 4, background: 'var(--border)', borderRadius: 3, overflow: 'hidden', marginTop: 3 }}><div style={{ width: `${item.percent}%`, height: '100%', background: '#6366f1' }} /></div>}{item.error && <div style={{ color: '#ef4444' }}>{item.error}</div>}</div>)}
              </div>}
              {attachedDocuments.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 7 }}>
                {attachedDocuments.map(document => <span key={document.id} title={document.mime_type?.startsWith('image/') ? 'Image ready for AI' : 'Text indexed and ready for AI'} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 6px', background: 'var(--bg-4)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: 'var(--text-secondary)' }}>{document.mime_type?.startsWith('image/') ? <ImageIcon size={11} /> : <FileText size={11} />}{document.filename} · Ready ✓<button type="button" aria-label={`Remove ${document.filename}`} onClick={() => removeAttachment(document)} style={{ border: 0, background: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>×</button></span>)}
              </div>}
              {attachmentError && <div role="alert" style={{ color: '#ef4444', fontSize: 11, marginBottom: 6 }}>{attachmentError}</div>}

              <div style={{
                display: 'flex', gap: 8, alignItems: 'flex-end',
                background: streaming ? 'var(--bg-3)' : 'var(--bg-4)',
                border: `1px solid ${streaming ? '#6366f155' : 'var(--border)'}`,
                borderRadius: 12,
                padding: streaming ? '8px 10px' : '8px 10px 8px 14px',
                transition: 'background 0.2s, border-color 0.2s',
              }}>
                {streaming ? (
                  /* Status row replaces textarea while working */
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minHeight: 36, paddingLeft: 6 }}>
                    {[0,1,2].map(i => (
                      <span key={i} style={{
                        display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                        background: '#6366f1', flexShrink: 0,
                        animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                      }} />
                    ))}
                    <span style={{ fontSize: 13, color: '#6366f1', fontWeight: 500 }}>
                      {streamStatus === 'thinking' && 'Thinking…'}
                      {streamStatus === 'writing' && 'Writing…'}
                      {streamStatus === 'creating' && 'Creating cards…'}
                      {streamStatus === 'reading page…' && 'Reading page…'}
                      {streamStatus === 'searching images…' && 'Searching images…'}
                      {streamStatus === 'generating image…' && 'Generating image…'}
                      {streamStatus === 'checking parent context…' && 'Checking parent context…'}
                    </span>
                  </div>
                ) : (
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder={modifyProposal ? 'What should change in the proposal?' : 'Ask about your plan…'}
                    rows={1}
                    style={{
                      flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none',
                      color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit',
                      resize: 'none', lineHeight: 1.5, maxHeight: 120, overflowY: 'auto',
                    }}
                    onInput={e => {
                      e.target.style.height = '0px';
                      e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                    }}
                  />
                )}
                {!streaming && <button type="button" title="Attach image or document" aria-label="Attach image or document" onClick={() => attachmentInputRef.current?.click()} disabled={uploadingDocument} style={{ border: 0, background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 4, alignSelf: 'center' }}><Paperclip size={16} /></button>}
                {streaming ? (
                  <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={stop}
                    title="Stop"
                    style={sendBtn('#ef4444')}>
                    <RotateCcw size={14} />
                  </motion.button>
                ) : (
                  <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                    onClick={send} disabled={(!input.trim() && !attachedDocuments.length) || uploadingDocument}
                    style={sendBtn((input.trim() || attachedDocuments.length) && !uploadingDocument ? '#6366f1' : 'var(--bg-4)')}>
                    <Send size={14} color={(input.trim() || attachedDocuments.length) && !uploadingDocument ? 'white' : 'var(--text-faint)'} />
                  </motion.button>
                )}
              </div>
              {modifyProposal && !streaming && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
                <span>Revising “{modifyProposal.payload.title}” — original stays pending.</span>
                <button type="button" onClick={() => { setModifyProposal(null); setInput(''); }} style={{ border: 'none', background: 'none', color: '#8b5cf6', cursor: 'pointer' }}>Stop revising</button>
              </div>}
              <div style={{ fontSize: 10, color: 'var(--text-faint)', textAlign: 'center', marginTop: 5 }}>
                {!streaming && (uploadingDocument ? 'Reading document…' : 'Enter to send · Shift+Enter for newline')}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Intercom-style bubble ────────────────────────────────────────── */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="bubble"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.93 }}
            onClick={() => setOpen(true)}
            title="Open AI Research sidebar"
            style={{
              position: 'fixed', right: 24, bottom: 24, zIndex: 100,
              width: 52, height: 52, borderRadius: '50%', border: 'none',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 20px rgba(99,102,241,0.5), 0 2px 8px rgba(0,0,0,0.3)',
            }}
          >
            <Sparkles size={22} color="white" />
            {/* Unread dot */}
            {unread && (
              <motion.div
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                style={{
                  position: 'absolute', top: 2, right: 2,
                  width: 12, height: 12, borderRadius: '50%',
                  background: '#ef4444', border: '2px solid var(--bg)',
                }}
              />
            )}
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
});

export default AiSidebar;

// ── Individual chat message ───────────────────────────────────────────────────
function stripXmlToolCalls(text) {
  // Remove any hallucinated <function_calls>...</function_calls> or <invoke>...</invoke> blocks
  return text
    .replace(/<function_calls>.*?<\/function_calls>/gs, '')
    .replace(/<invoke[^>]*>.*?<\/invoke>/gs, '')
    .replace(/<parameter[^>]*>.*?<\/parameter>/gs, '')
    .replace(/\s*<\/?function_calls>\s*/g, '')
    .trim();
}

function ChatMessage({ msg, currentUserId, streaming, onCreateBox, onCardCreated, focusedCard, token, API, onPropose, planId, parentId, plan, onPlanReviewChange, onPlanReviewModify }) {
  const isUser = msg.role === 'user';
  const isMine = isUser && (!msg.authorUserId || msg.authorUserId === currentUserId);
  const content = isUser ? (msg.content || '') : stripXmlToolCalls(msg.content || '');
  const attachedImageIds = isUser
    ? [...(msg.apiContent || msg.content || '').matchAll(/\[Attached images: ([^\]]+)\]/g)]
      .flatMap(match => match[1].split(',').map(id => id.trim()).filter(id => /^[0-9a-f-]{36}$/i.test(id)))
    : [];

  // Split assistant messages into paragraphs/sections for per-block card creation
  const blocks = isUser ? null : parseBlocks(content);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: isMine ? 'flex-end' : 'flex-start' }}>
      {isUser ? (
        <div style={{ maxWidth: '90%', display: 'flex', flexDirection: 'column', gap: 6, alignItems: isMine ? 'flex-end' : 'flex-start' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 10, fontWeight: 700 }}>{isMine ? 'You' : (msg.authorEmail || 'Collaborator')}</span>
          {/* Card context snippet shown above the user's question */}
          {msg.cardContext && (
            <div style={{
              width: '100%',
              borderRadius: '12px 12px 4px 12px',
              border: '1px solid #8b5cf640',
              background: 'linear-gradient(135deg, #8b5cf610, #6366f108)',
              padding: '8px 12px',
              fontSize: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
                <Sparkles size={10} color="#8b5cf6" />
                <span style={{ fontSize: 10, fontWeight: 700, color: '#8b5cf6', letterSpacing: '0.3px' }}>{msg.cardContext.title}</span>
              </div>
              {msg.cardContext.content && (
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.55,
                  display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {msg.cardContext.content.replace(/[#*`]/g, '')}
                </div>
              )}
            </div>
          )}
          {/* The user's actual question */}
          <div style={{
            background: isMine ? '#6366f1' : 'var(--bg-4)', color: isMine ? 'white' : 'var(--text-primary)', borderRadius: isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
            padding: '9px 13px', fontSize: 13, lineHeight: 1.55,
            wordBreak: 'break-word',
          }}>
            {content}
            {attachedImageIds.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 9 }}>
                {attachedImageIds.map(id => <ChatImageAttachment key={id} id={id} planId={planId} token={token} api={API} />)}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ width: '100%' }}>
          {msg.authorEmail && <div style={{ color: 'var(--text-muted)', fontSize: 10, fontWeight: 700, marginBottom: 3 }}>AI · replying to {msg.authorEmail}</div>}
          {streaming && !content && (
            <div style={{ display: 'flex', gap: 5, padding: '8px 0' }}>
              {[0,1,2].map(i => (
                <motion.div key={i}
                  animate={{ scale: [1,1.4,1], opacity: [0.3,1,0.3] }}
                  transition={{ repeat: Infinity, duration: 0.9, delay: i*0.15 }}
                  style={{ width: 6, height: 6, borderRadius: '50%', background: '#6366f1' }} />
              ))}
            </div>
          )}
          {blocks && blocks.map((block, i) => (
            <MessageBlock key={i} block={block} onCreateBox={onCreateBox} onCardCreated={onCardCreated} />
          ))}
          {msg.toolResults?.length > 0 && (
            <div style={{ marginTop: 8, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}>
              {msg.toolResults.map((r, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 12px',
                  background: r.ok ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                  borderBottom: i < msg.toolResults.length - 1 ? '1px solid var(--border)' : 'none',
                  fontSize: 12,
                }}>
                  <span style={{ fontSize: 14 }}>{r.ok ? '✅' : '❌'}</span>
                  <span style={{ color: r.ok ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                    {r.ok
                      ? `"${r.title}" ${r.action === 'updated' ? 'updated' : 'added to canvas'}`
                      : `Failed to ${r.action === 'updated' ? 'update' : 'create'} "${r.title}"${r.error ? `: ${r.error}` : ''}`
                    }
                  </span>
                </div>
              ))}
            </div>
          )}
          {/* Update Card button — shown on assistant messages when sidebar was opened from a card */}
          {!streaming && focusedCard?.id && content && (
            <UpdateCardButton
              cardId={focusedCard.id}
              cardTitle={focusedCard.title}
              content={content}
              token={token}
              API={API}
              onCardCreated={onCardCreated}
              onPropose={onPropose}
              planId={planId}
              parentId={parentId}
            />
          )}
          {msg.imageResults?.map((result, ri) => (
            <ImageResultBlock key={ri} result={result} onCreateBox={onCreateBox} />
          ))}
          {msg.planReview && msg.persistedId && <div style={{ marginTop: 9 }}>
            <PlanReviewInline plan={plan} token={token} suggestion={msg.planReview} messageId={msg.persistedId}
              disabled={streaming} onSuggestionChange={onPlanReviewChange} onApplied={onCardCreated} onModify={onPlanReviewModify} />
          </div>}
        </div>
      )}
    </div>
  );
}

function ChatImageAttachment({ id, planId, token, api }) {
  const [image, setImage] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    let objectUrl;
    fetch(`${api}/api/plans/${planId}/documents/${id}/file`, {
      headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
    }).then(async response => {
      if (!response.ok) throw new Error('Image unavailable');
      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) throw new Error('Not an image');
      if (controller.signal.aborted) return;
      objectUrl = URL.createObjectURL(blob);
      const encodedName = response.headers.get('Content-Disposition')?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
      setImage({ url: objectUrl, name: encodedName ? decodeURIComponent(encodedName) : 'Attached image' });
    }).catch(error => {
      if (error.name !== 'AbortError') setImage({ error: true });
    });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id, planId, token, api]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = event => { if (event.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (image?.error) return <span style={{ fontSize: 11, opacity: 0.85 }}>Image unavailable</span>;
  if (!image) return <span style={{ fontSize: 11, opacity: 0.85 }}>Loading image…</span>;

  return <>
    <button type="button" title={`Open ${image.name}`} aria-label={`Open ${image.name}`}
      onClick={() => setOpen(true)} style={{ padding: 0, border: '1px solid #ffffff80', borderRadius: 7, background: 'transparent', cursor: 'zoom-in', overflow: 'hidden' }}>
      <img src={image.url} alt={image.name} style={{ display: 'block', width: 86, height: 86, objectFit: 'cover' }} />
    </button>
    {open && createPortal(<div role="presentation" onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.82)', padding: 20, cursor: 'zoom-out' }}>
      <button type="button" onClick={() => setOpen(false)} aria-label="Close image preview" style={{ position: 'absolute', top: 16, right: 16, border: 0, borderRadius: 8, padding: 8, background: '#fff', color: '#111', cursor: 'pointer' }}><X size={20} /></button>
      <img src={image.url} alt={image.name} onClick={event => event.stopPropagation()} style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', cursor: 'default' }} />
    </div>, document.body)}
  </>;
}

function stripAiTrailingQuestion(text) {
  // Remove trailing paragraph(s) that are just the AI offering to do something
  const lines = text.trimEnd().split('\n');
  // Walk backwards and drop trailing lines that look like AI filler/offers
  const offerPattern = /would you like|want me to|shall i|should i|let me know|feel free|any questions|need anything|can i help|do you want|ready to|want to keep|draft a|next steps\?$/i;
  while (lines.length > 0) {
    const last = lines[lines.length - 1].trim();
    if (!last || offerPattern.test(last)) {
      lines.pop();
    } else {
      break;
    }
  }
  return lines.join('\n').trimEnd();
}

function UpdateCardButton({ cardId, cardTitle, content, token, API, onPropose, planId, parentId }) {
  const [state, setState] = React.useState('idle'); // idle | saving | done | error
  const cleanContent = stripAiTrailingQuestion(content);

  async function handleUpdate() {
    setState('saving');
    try {
      const resp = await fetch(`${API}/api/plans/${planId}/ai-proposals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ action: 'update', cardId, parentId, payload: { title: cardTitle, content: cleanContent, summary: 'Replace card content with this AI response' } }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      onPropose(await resp.json());
      setState('done');
      setTimeout(() => setState('idle'), 3000);
    } catch (e) {
      console.error('[UpdateCard] failed:', e);
      setState('error');
      setTimeout(() => setState('idle'), 3000);
    }
  }

  const label = state === 'saving' ? 'Preparing…' : state === 'done' ? 'Review proposed update' : state === 'error' ? 'Failed — retry?' : `↑ Propose update to "${cardTitle}"`;
  const bg = state === 'done' ? 'rgba(16,185,129,0.12)' : state === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(99,102,241,0.08)';
  const border = state === 'done' ? 'rgba(16,185,129,0.3)' : state === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(99,102,241,0.2)';
  const color = state === 'done' ? '#10b981' : state === 'error' ? '#ef4444' : '#6366f1';

  return (
    <button
      onClick={handleUpdate}
      disabled={state === 'saving' || state === 'done'}
      style={{
        marginTop: 10,
        width: '100%',
        padding: '8px 14px',
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 8,
        color,
        fontSize: 12,
        fontWeight: 600,
        cursor: state === 'saving' || state === 'done' ? 'default' : 'pointer',
        textAlign: 'center',
        transition: 'all 0.2s',
      }}
    >
      {label}
    </button>
  );
}

function ImageResultBlock({ result }) {
  const [lightbox, setLightbox] = React.useState(null);

  // Escape to close lightbox
  React.useEffect(() => {
    if (lightbox === null) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') setLightbox(l => l !== null && l < allUrls.length - 1 ? l + 1 : l);
      if (e.key === 'ArrowLeft')  setLightbox(l => l !== null && l > 0 ? l - 1 : l);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox]);

  // Handle AI-generated image (single image, different layout)
  if (result.type === 'image_generated') {
    if (!result.url) {
      return (
        <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--bg-4)', borderRadius: 10, fontSize: 12, color: 'var(--text-muted)' }}>
          Image generation failed: {result.error || 'unknown error'}
        </div>
      );
    }
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--text-faint)', marginBottom: 6, fontWeight: 600 }}>
          Generated image{result.referenceCount ? ` using ${result.referenceCount} uploaded reference${result.referenceCount === 1 ? '' : 's'}` : ''} — drag to canvas or card
        </div>
        <div
          draggable
          onDragStart={e => {
            e.dataTransfer.setData('text/x-ideacanvas-image-url', result.url);
            e.dataTransfer.setData('text/x-ideacanvas-image-title', result.card_title || result.prompt?.slice(0, 50) || 'Generated image');
            e.dataTransfer.effectAllowed = 'copy';
          }}
          onClick={() => setLightbox(0)}
          title={result.prompt + ' — drag to canvas or card'}
          style={{ borderRadius: 10, overflow: 'hidden', background: 'var(--bg-4)', cursor: 'grab', maxWidth: 280 }}
        >
          <img
            src={result.url} alt={result.prompt}
            style={{ width: '100%', display: 'block', borderRadius: 10 }}
            onError={e => { e.target.parentElement.style.display = 'none'; }}
          />
        </div>
        {/* Lightbox */}
        <AnimatePresence>
          {lightbox === 0 && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setLightbox(null)}
              style={{
                position: 'fixed', inset: 0, zIndex: 2000,
                background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <img src={result.url} alt={result.prompt} style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12 }} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  const allUrls = (result.images || []).map(img => typeof img === 'string' ? img : img.url);

  if (!allUrls.length) {
    return (
      <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--bg-4)', borderRadius: 10, fontSize: 12, color: 'var(--text-muted)' }}>
        No images found for "{result.query}"
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, color: 'var(--text-faint)', marginBottom: 6, fontWeight: 600 }}>
        Images for "{result.query}" — click to view, drag to canvas or card
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5 }}>
        {result.images.map((img, i) => {
          const url = typeof img === 'string' ? img : img.url;
          const title = typeof img === 'string' ? result.query : (img.title || result.query);
          return (
            <div
              key={i}
              draggable
              onDragStart={e => {
                e.dataTransfer.setData('text/x-ideacanvas-image-url', url);
                e.dataTransfer.setData('text/x-ideacanvas-image-title', title);
                e.dataTransfer.effectAllowed = 'copy';
              }}
              onClick={() => setLightbox(i)}
              title={title + ' — drag to canvas or card'}
              style={{
                borderRadius: 8, overflow: 'hidden', background: 'var(--bg-4)',
                aspectRatio: '1', cursor: 'grab',
              }}
            >
              <img
                src={url} alt={title}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
                onError={e => { e.target.parentElement.style.display = 'none'; }}
              />
            </div>
          );
        })}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            onDragOver={e => e.stopPropagation()}
            onDrop={e => e.stopPropagation()}
            style={{
              position: 'fixed', inset: 0, zIndex: 2000,
              background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {lightbox > 0 && (
              <motion.button whileTap={{ scale: 0.9 }}
                onClick={e => { e.stopPropagation(); setLightbox(l => l - 1); }}
                style={lbBtn('left')}>
                <ChevronLeft size={22} />
              </motion.button>
            )}
            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.93 }} animate={{ opacity: 1, scale: 1 }}
              src={allUrls[lightbox]} alt=""
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: '88vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 12, boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }}
            />
            {lightbox < allUrls.length - 1 && (
              <motion.button whileTap={{ scale: 0.9 }}
                onClick={e => { e.stopPropagation(); setLightbox(l => l + 1); }}
                style={lbBtn('right')}>
                <ChevronRight size={22} />
              </motion.button>
            )}
            <motion.button whileTap={{ scale: 0.9 }}
              onClick={() => setLightbox(null)}
              style={{ position: 'absolute', top: 18, right: 18, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: 8, cursor: 'pointer', color: 'white', display: 'flex' }}>
              <X size={18} />
            </motion.button>
            <div style={{ position: 'absolute', bottom: 18, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
              {lightbox + 1} / {allUrls.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const lbBtn = (side) => ({
  position: 'absolute', [side]: 18, top: '50%', transform: 'translateY(-50%)',
  background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 10, padding: 10, cursor: 'pointer', color: 'white', display: 'flex',
});

// Parse assistant content into discrete blocks (sections split by headings, or paragraphs)
function parseBlocks(content) {
  if (!content) return [];
  // Split on markdown headings or double newlines between substantial paragraphs
  const sections = content.split(/\n(?=#{1,3}\s)/);
  if (sections.length > 1) {
    return sections.map(s => ({ type: 'text', text: s.trim() })).filter(b => b.text);
  }
  // No headings — return as a single block
  return [{ type: 'text', text: content }];
}

function MessageBlock({ block, onCreateBox, onCardCreated }) {
  const [hovered, setHovered] = useState(false);
  const [blockCopied, setBlockCopied] = useState(false);
  const accent = '#6366f1';

  async function handleCopyBlock() {
    try {
      await navigator.clipboard.writeText(block.text);
      setBlockCopied(true);
      setTimeout(() => setBlockCopied(false), 2000);
    } catch (e) {}
  }

  // A heading becomes the card title; do not save the same heading in its body.
  const headingMatch = block.text.match(/^#{1,3}\s+([^\r\n]+)\r?\n?/);
  const suggestedTitle = headingMatch?.[1]?.trim() || block.text.slice(0, 60).split('\n')[0].replace(/[#*]/g, '').trim();
  const cardBody = headingMatch ? block.text.slice(headingMatch[0].length).trimStart() : block.text;

  async function addTextCard() {
    await onCreateBox({ title: suggestedTitle, content_type: 'text', content: cardBody });
    onCardCreated?.();
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setHovered(false); }}
      style={{
        position: 'relative',
      }}
    >
      <div className="prose ai-message-content" style={{ fontSize: 13, lineHeight: 1.7 }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{block.text}</ReactMarkdown>
      </div>

      {/* Reserve the footer's full height so showing actions never moves the response. */}
      <div
        style={{ display: 'flex', flexWrap: 'wrap', gap: 5, margin: '4px 0',
          opacity: hovered ? 1 : 0, pointerEvents: hovered ? 'auto' : 'none',
          transition: 'opacity 0.15s' }}
      >
        {/* Add as text card */}
        <CardChip icon={<FileText size={10} />} label="+ Add as card" onClick={addTextCard} color={accent} />

        {/* Copy this block */}
        <CardChip
          icon={blockCopied ? <Check size={10} /> : <Copy size={10} />}
          label={blockCopied ? 'Copied!' : 'Copy'}
          onClick={handleCopyBlock}
          color={blockCopied ? '#10b981' : '#64748b'}
        />
      </div>
    </div>
  );
}

function CardChip({ icon, label, onClick, color }) {
  return (
    <motion.button
      whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '3px 9px', borderRadius: 20, border: `1px solid ${color}35`,
        background: `${color}12`, color, fontSize: 11, fontWeight: 600,
        cursor: 'pointer', fontFamily: 'inherit',
        maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}
    >
      {icon}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
    </motion.button>
  );
}

function EmptyChat() {
  return (
    <div style={{ textAlign: 'center', padding: '24px 8px' }}>
      <div style={{
        width: 44, height: 44, borderRadius: 14, margin: '0 auto 12px',
        background: 'linear-gradient(135deg, #6366f133, #8b5cf633)',
        border: '1px solid #6366f130',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Sparkles size={20} color="#6366f1" style={{ opacity: 0.7 }} />
      </div>
      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
        Start with an idea
      </p>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 12 }}>
        Add cards yourself with <strong>Add card</strong> on the canvas. Give each card a topic, note, link, or image, then open it to build out the details.
      </p>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Or chat with AI here like you would in any AI app: ask a question, explore an idea, or request research. Hover over a response to copy it or add it as a card.
      </p>
    </div>
  );
}

const sendBtn = (bg) => ({
  width: 32, height: 32, borderRadius: 8, border: 'none',
  background: bg, cursor: 'pointer', display: 'flex',
  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  transition: 'background 0.15s',
});

function IconBtn({ onClick, title, children }) {
  return (
    <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
      onClick={onClick} title={title}
      style={{
        background: 'var(--bg-4)', border: '1px solid var(--border)',
        borderRadius: 7, padding: 6, cursor: 'pointer',
        color: 'var(--text-muted)', display: 'flex',
      }}>
      {children}
    </motion.button>
  );
}
